import { useEffect, useState } from "react";
import { getListing, saveListing, type ListingField, type ListingView } from "./actions";

// The listing editor, opened from a product's detail view. Every field shows four things: what the vendor sent, the console's dynamic default, what you
// typed (leave it empty to use the default), and the final value that will be published. "Reset" removes your override so the field goes back to its
// default; "Reset all" does that for every field. The corpus is never changed: overrides live in their own layer and every save is kept in a history.

const FIELDS: { key: ListingField; label: string; kind?: "area" | "robots" | "price"; hint?: string; limit?: number }[] = [
  { key: "title", label: "Title", hint: "Merchant Center allows 150 characters", limit: 150 },
  { key: "slug", label: "URL slug", hint: "lowercase letters, digits and hyphens" },
  { key: "brand", label: "Brand" },
  { key: "category", label: "Category" },
  { key: "description", label: "Description", kind: "area" },
  { key: "sell_price", label: "Selling price", kind: "price", hint: "empty = the pricing rule; never below MAP" },
  { key: "seo_title", label: "SEO title", hint: "about 60 characters", limit: 60 },
  { key: "seo_description", label: "SEO description", kind: "area", hint: "about 155 characters", limit: 155 },
  { key: "focus_keyphrase", label: "Focus keyphrase" },
  { key: "robots", label: "Search engines", kind: "robots" },
  { key: "canonical_path", label: "Canonical path", hint: "empty = the product's own URL" },
];

export function ListingEditor({ docId, onSaved }: { docId: string; onSaved: () => void }) {
  const [v, setV] = useState<ListingView | null>(null);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const r = await getListing(docId);
    if (!r.ok) { setError(r.error); return; }
    setError(null); setV(r.data);
    setDraft(Object.fromEntries(Object.entries(r.data.override).map(([k, x]) => [k, String(x)])));
  };
  useEffect(() => { setV(null); setMsg(null); load(); }, [docId]);

  if (error && !v) return <div className="panel error">{error}</div>;
  if (!v) return <div className="meta">Loading the listing…</div>;

  const changed = FIELDS.filter((f) => (draft[f.key] ?? "") !== String(v.override[f.key] ?? ""));
  const save = async (fields: Record<string, string>, resetAll = false) => {
    setBusy(true); setMsg(null);
    const r = await saveListing(docId, fields, resetAll);
    setBusy(false);
    if (!r.ok) { setError(r.error); return; }
    setError(null); setMsg(resetAll ? "Everything is back to its default." : "Saved."); await load(); onSaved();
  };
  const dynamicOf = (k: ListingField) => (k === "sell_price" ? (v.price.rule != null ? v.price.rule.toFixed(2) : "no cost: no price") : v.dynamic[k] ?? "");
  const finalOf = (k: ListingField) => (k === "sell_price" ? (v.price.override ?? v.price.rule) : v.final[k]?.value);

  return (
    <div style={{ marginTop: 8 }}>
      <h3 style={{ margin: "8px 0 4px" }}>Edit listing</h3>
      <p className="meta" style={{ margin: "0 0 6px" }}>
        Leave a field empty to use the default. {v.editedBy ? `Last edited by ${v.editedBy} on ${v.editedAt.slice(0, 10)}.` : "Never edited."}
      </p>
      {error && <div className="panel error">{error}</div>}
      {msg && <div className="panel">{msg}</div>}
      {FIELDS.map((f) => {
        const val = draft[f.key] ?? "";
        const overridden = String(v.override[f.key] ?? "") !== "";
        const fin = String(finalOf(f.key) ?? "");
        return (
          <div key={f.key} className="panel" style={{ marginBottom: 8 }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
              <b>{f.label}</b>
              {overridden && <button type="button" disabled={busy} onClick={() => save({ [f.key]: "" })}>Reset to default</button>}
            </div>
            {v.feed[f.key] !== undefined && <div className="meta" style={{ margin: "4px 0" }}>Vendor sent: <span className="mono">{v.feed[f.key] || "(empty)"}</span></div>}
            <div className="meta" style={{ margin: "4px 0" }}>Default: <span className="mono">{dynamicOf(f.key) || "(empty)"}</span></div>
            {f.kind === "area"
              ? <textarea rows={3} style={{ width: "100%" }} value={val} placeholder="your override" onChange={(e) => setDraft({ ...draft, [f.key]: e.target.value })} />
              : f.kind === "robots"
              ? <select value={val} onChange={(e) => setDraft({ ...draft, [f.key]: e.target.value })}>
                  <option value="">Default (index, follow)</option><option value="index,follow">index, follow</option>
                  <option value="noindex,follow">noindex, follow</option><option value="noindex,nofollow">noindex, nofollow</option></select>
              : <input style={{ width: "100%" }} type={f.kind === "price" ? "number" : "text"} step={f.kind === "price" ? "0.01" : undefined} value={val}
                       placeholder="your override" onChange={(e) => setDraft({ ...draft, [f.key]: e.target.value })} />}
            <div className="meta" style={{ margin: "4px 0 0" }}>
              Final ({overridden ? "your override" : "default"}): <b>{fin || "(empty)"}</b>
              {f.limit && (val || fin) ? ` · ${(val || fin).length}/${f.limit} characters` : ""}{f.hint ? ` · ${f.hint}` : ""}
            </div>
          </div>
        );
      })}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <button type="button" disabled={busy || changed.length === 0}
                onClick={() => save(Object.fromEntries(changed.map((f) => [f.key, draft[f.key] ?? ""])))}>{busy ? "Saving…" : `Save ${changed.length || ""} change${changed.length === 1 ? "" : "s"}`}</button>
        <button type="button" disabled={busy || Object.keys(v.override).length === 0} onClick={() => save({}, true)}>Reset all to defaults</button>
      </div>
    </div>
  );
}
